import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Avatar, useTheme, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CustomDialog from '@ui/components/common/Dialog';
import { HFilledTextField } from '@ui/components/common/TextField';
import DataTable from '@ui/components/common/DataTable';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@application/store/useStore';
import { centeredRowStack } from '@ui/style/style';
import { createInterestAsync, deleteInterestAsync, getAllInterestsAsync, getInterestByIdAsync, updateInterestAsync } from '@application/features/interests/interestsThunk';
import { clearSelectedInterest } from '@application/features/interests/interestsSlice';
import { useParams } from 'react-router-dom';
import { getEventInterestsByEventIdAsync } from '@application/features/eventInterests/eventInterestsThunk';
import { getPeopleInterestsByPeopleIdAsync } from '@application/features/peopleInterests/peopleInterestsThunk';
import { Delete } from 'react-iconly';
// Mock data - replace with actual API call
const mockInterests = [
    { id: '1', name: 'Football', numberOfPeople: 25, numberOfEvents: 25 },
    { id: '2', name: 'Adventure', numberOfPeople: 53, numberOfEvents: 58 },
    { id: '3', name: 'Racing', numberOfPeople: 12, numberOfEvents: 8 },
    { id: '4', name: 'Travel', numberOfPeople: 45, numberOfEvents: 32 },
    { id: '5', name: 'Cricket', numberOfPeople: 18, numberOfEvents: 15 },
    { id: '6', name: 'Volunteering', numberOfPeople: 30, numberOfEvents: 22 },
    { id: '7', name: 'Coding', numberOfPeople: 20, numberOfEvents: 10 },
    { id: '8', name: 'Robotics', numberOfPeople: 15, numberOfEvents: 5 }
];
const InterestsTab = ({ showFilters = false, showSearch = true, title, type = 'settings' }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const [openFilterDialog, setOpenFilterDialog] = useState(false);
    const [filters, setFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({});
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [interestTitle, setInterestTitle] = useState('');
    const [isEdit, setisEdit] = useState(false);
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [selectedTagId, setSelectedTagId] = useState(null);
    const { selectedCompanyId } = useSelector((state) => state.company);
    const { interests, selectedInterest } = useSelector((state) => state.interests);
    const { paginatedeventInterests } = useSelector((state) => state.eventInterests);
    const { paginatedPeopleInterests } = useSelector((state) => state.peopleInterests);
    const params = useParams();
    // Determine which type of page we're on
    const id = params.eventId || params.id;
    const getDataSource = () => {
        if (type === 'settings')
            return interests?.items || [];
        if (type === 'event')
            return paginatedeventInterests?.items || [];
        if (type === 'people')
            return paginatedPeopleInterests?.items || [];
        return [];
    };
    const gettotalCountforPagination = () => {
        if (type === 'settings')
            return interests?.totalCount || 0;
        if (type === 'event')
            return paginatedeventInterests?.totalCount || 0;
        if (type === 'people')
            return paginatedPeopleInterests?.totalCount || 0;
        return 0;
    };
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const dispatch = useAppDispatch();
    // Dialog handlers
    const handleOpenAddDialog = () => {
        if (type === 'settings') {
            setOpenAddDialog(true);
            setInterestTitle('');
        }
    };
    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setInterestTitle('');
        if (isEdit) {
            dispatch(clearSelectedInterest());
            setisEdit(false);
        }
    };
    const GetAllInterest = () => {
        if (type === 'settings') {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue || undefined
            };
            dispatch(getAllInterestsAsync(request));
        }
        else if (type === 'event') {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                eventId: id ? id : ''
            };
            dispatch(getEventInterestsByEventIdAsync(request));
        }
        else if (type === 'people') {
            const request = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                personId: id ? id : ''
            };
            dispatch(getPeopleInterestsByPeopleIdAsync(request));
        }
    };
    const handleAddInterest = () => {
        if (interestTitle.trim()) {
            const newInterest = {
                title: interestTitle.trim()
            };
            dispatch(createInterestAsync(newInterest))
                .unwrap()
                .then(() => {
                GetAllInterest();
            });
            handleCloseAddDialog();
        }
    };
    const handleEditInterest = () => {
        if (interestTitle.trim() && selectedInterest) {
            const UpdateInterest = {
                id: selectedInterest.id,
                title: interestTitle.trim()
            };
            dispatch(updateInterestAsync(UpdateInterest))
                .unwrap()
                .then(() => {
                GetAllInterest();
            });
            handleCloseAddDialog();
        }
    };
    useEffect(() => {
        if (selectedCompanyId && selectedCompanyId.length > 0) {
            GetAllInterest();
        }
    }, [
        dispatch,
        paginationModel.page,
        paginationModel.pageSize,
        searchValue,
        filters,
        selectedCompanyId,
        id
    ]);
    const handleEdit = (row) => {
        // Dispatch the async action to fetch the interest data by ID
        if (type === 'settings') {
            dispatch(getInterestByIdAsync(row.id)).unwrap();
        }
    };
    useEffect(() => {
        if (type === 'settings') {
            if (selectedInterest) {
                // Once selectedInterest is updated in Redux store, set the title and open the dialog
                setInterestTitle(selectedInterest.title);
                setisEdit(true);
                setOpenAddDialog(true);
            }
        }
    }, [selectedInterest]);
    const handleDelete = (row) => {
        if (type === 'settings' && row) {
            setSelectedTagId(row.id);
            setOpenDeleteConfirmation(true);
        }
    };
    const handleConfirmDelete = () => {
        if (!selectedTagId)
            return;
        dispatch(deleteInterestAsync(selectedTagId))
            .unwrap()
            .then(() => {
            GetAllInterest();
            toast.success(t('settings.tagDeletedSuccessfully') || 'Tag deleted successfully');
        })
            .catch((error) => {
            const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                ? error.message
                : undefined;
            toast.error(errorMessage || t('settings.failedToDeleteTag') || 'Failed to delete tag');
        });
        setOpenDeleteConfirmation(false);
        setSelectedTagId(null);
    };
    // Table columns configuration
    let columns = [
        {
            key: 'name',
            label: t('settings.tags'),
            render: (row) => _jsx(Typography, { variant: 'body2', children: row?.title })
        },
        {
            key: 'numberOfPeople',
            label: t('settings.numberOfPeople'),
            align: 'center',
            render: (row) => (_jsx(Avatar, { sx: {
                    width: 32,
                    height: 32,
                    bgcolor: '#E3F2FD',
                    color: '#1976D2',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    mx: 'auto'
                }, children: row?.peopleCount }))
        },
        {
            key: 'numberOfEvents',
            label: t('settings.numberOfEvents'),
            align: 'center',
            render: (row) => (_jsx(Avatar, { sx: {
                    width: 32,
                    height: 32,
                    bgcolor: '#E8F5E9',
                    color: '#388E3C',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    mx: 'auto'
                }, children: row?.eventCount }))
        }
    ];
    // Filter columns based on type
    columns = columns.filter((col) => {
        if (type === 'event')
            return col.key === 'name'; // hide both numbers
        if (type === 'people')
            return col.key !== 'numberOfPeople'; // hide numberOfPeople
        return true; // setting = show all
    });
    return (_jsxs(Box, { children: [_jsx(DataTable, { data: getDataSource(), columns: columns, getRowId: (row) => row.id, title: title, searchValue: showSearch ? searchValue : '', onSearchChange: showSearch ? setSearchValue : undefined, searchPlaceholder: showSearch
                    ? t('placeholders.search') || t('common.search') || 'Search...'
                    : undefined, showSearchFilter: showSearch, showFilterButton: type !== 'settings', onFilterClick: showFilters
                    ? () => {
                        setTempFilters(filters);
                        setOpenFilterDialog(true);
                    }
                    : undefined, filterLabel: showFilters ? t('events.filters') || t('actions.filters') || 'Filters' : undefined, filters: showFilters ? filters : undefined, onClearFilters: showFilters
                    ? () => {
                        setFilters({});
                        setTempFilters({});
                    }
                    : undefined, showFilterClearButton: showFilters && Object.keys(filters).length > 0, showAddButton: type === 'settings' ? true : false, addButtonLabel: t('settings.newTag'), onAddClick: handleOpenAddDialog, onEdit: type === 'settings' ? handleEdit : undefined, onDelete: type === 'settings' ? handleDelete : undefined, emptyMessage: t('settings.noInterestsFound'), toolbarPosition: 'right', pagination: true, page: paginationModel.page, rowsPerPage: paginationModel.pageSize, rowsPerPageOptions: [5, 10, 25, 50], onPageChange: (newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage })), onRowsPerPageChange: (newRowsPerPage) => setPaginationModel((prev) => ({ ...prev, pageSize: newRowsPerPage, page: 0 })), totalCount: gettotalCountforPagination(), ...(type === 'settings' && {
                    menuItems: [
                        {
                            label: t('actions.delete'),
                            icon: _jsx(Delete, { style: { width: 20, height: 20 } }),
                            color: theme.palette.error.light,
                            onClick: (row) => {
                                if (handleDelete)
                                    handleDelete(row);
                            },
                            danger: true
                        }
                    ]
                }) }), _jsx(CustomDialog, { backgroundVariant: 'white', open: openAddDialog, onClose: handleCloseAddDialog, maxWidth: 'sm', titleWithIcon: isEdit ? t('settings.editTag') : t('settings.addNewTag'), titleIcon: '/assets/images/interest.svg', actions: _jsxs(Box, { sx: { display: 'flex', gap: 2, width: '100%' }, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', onClick: handleCloseAddDialog, sx: {
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.primary
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'primary', onClick: isEdit ? handleEditInterest : handleAddInterest, disabled: !interestTitle.trim(), children: isEdit ? t('actions.editNow') : t('actions.addNow') })] }), children: _jsx(Box, { sx: { p: 3 }, children: _jsx(HFilledTextField, { bg: 'grey8', label: t('settings.tagTitle'), value: interestTitle, onChange: (e) => setInterestTitle(e.target.value), fullWidth: true, autoFocus: true, onKeyDown: (e) => {
                            if (e.key === 'Enter' && interestTitle.trim()) {
                                e.preventDefault();
                                if (isEdit) {
                                    handleEditInterest();
                                }
                                else {
                                    handleAddInterest();
                                }
                            }
                        } }) }) }), _jsx(CustomDialog, { open: openDeleteConfirmation, title: t('common.deleteConfirmation'), onClose: () => {
                    setOpenDeleteConfirmation(false);
                    setSelectedTagId(null);
                }, backgroundVariant: 'white', actions: _jsx(_Fragment, { children: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => {
                                    setOpenDeleteConfirmation(false);
                                    setSelectedTagId(null);
                                }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, onClick: handleConfirmDelete, sx: {
                                    backgroundColor: theme.palette.error.main
                                }, children: t('actions.deleteNow') })] }) }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('common.deleteWarningMessage') }) }) })] }));
};
export default InterestsTab;
